import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AITutor from "@/components/AITutor";
import { CheckCircle, Lock, ChevronRight, ChevronLeft, ListChecks, Trophy } from "lucide-react";

export default function LessonPlayer() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/api/courses/${courseId}`);
    setCourse(data);
    if (!data.enrolled) {
      toast.error("You are not enrolled in this course");
      nav(`/courses/${data.slug}`);
      return;
    }
    const enr = (await api.get("/api/student/enrollments")).data
      .find((e) => e.course.id === data.id);
    if (enr) {
      setEnrollment(enr.enrollment);
      setActiveLessonId(enr.enrollment.last_lesson_id || data.modules[0]?.lessons[0]?.id);
    } else {
      setActiveLessonId(data.modules[0]?.lessons[0]?.id);
    }
  };

  useEffect(() => { load(); }, [courseId]);

  const flatLessons = useMemo(() => {
    if (!course) return [];
    return course.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, moduleId: m.id, moduleTitle: m.title })));
  }, [course]);

  const activeLesson = flatLessons.find((l) => l.id === activeLessonId);
  const activeIndex = flatLessons.findIndex((l) => l.id === activeLessonId);
  const completed = new Set(enrollment?.completed_lessons || []);

  const markComplete = async () => {
    if (!activeLesson) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/student/lesson/complete", { course_id: course.id, lesson_id: activeLesson.id });
      setEnrollment((prev) => ({ ...prev, completed_lessons: [...new Set([...(prev?.completed_lessons || []), activeLesson.id])], completion_percent: data.completion_percent, last_lesson_id: activeLesson.id }));
      toast.success("Marked complete");
    } finally { setBusy(false); }
  };

  const nextLesson = () => {
    if (activeIndex >= 0 && activeIndex < flatLessons.length - 1) {
      setActiveLessonId(flatLessons[activeIndex + 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const prevLesson = () => {
    if (activeIndex > 0) {
      setActiveLessonId(flatLessons[activeIndex - 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!course || !activeLesson) return <div className="py-24 text-center">Loading...</div>;

  const lastInModule = (() => {
    const idx = course.modules.findIndex((m) => m.id === activeLesson.moduleId);
    const mod = course.modules[idx];
    return mod.lessons[mod.lessons.length - 1].id === activeLesson.id;
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-12 gap-6" data-testid="lesson-player">
      <aside className="lg:col-span-3 bg-white border border-[#E5E7EB] rounded-xl overflow-hidden h-fit sticky top-24">
        <div className="p-4 border-b border-[#E5E7EB]">
          <Link to={`/courses/${course.slug}`} className="text-xs text-[#4B5563] hover:text-[#D92D20]">← {course.title}</Link>
          <div className="font-display font-bold mt-1 line-clamp-2">{course.title}</div>
          <div className="mt-2 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
            <div className="h-full bg-[#D92D20]" style={{ width: `${enrollment?.completion_percent || 0}%` }} />
          </div>
          <div className="text-xs text-[#4B5563] mt-1">{enrollment?.completion_percent || 0}% complete</div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {course.modules.map((m, mi) => (
            <div key={m.id} className="mb-2">
              <div className="px-2 py-1 label-eyebrow">Module {mi+1}</div>
              <div className="px-2 font-display font-bold text-sm">{m.title}</div>
              <ul className="mt-1">
                {m.lessons.map((l) => {
                  const done = completed.has(l.id);
                  const active = l.id === activeLessonId;
                  return (
                    <li key={l.id}>
                      <button
                        data-testid={`lesson-nav-${l.id}`}
                        onClick={() => setActiveLessonId(l.id)}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded-md flex items-center gap-2 ${active ? "bg-[#FECACA]/40 text-[#7F1D1D]" : "hover:bg-[#F3F4F6]"}`}
                      >
                        {done ? <CheckCircle className="w-4 h-4 text-[#064E3B] shrink-0" /> : <span className="w-4 h-4 rounded-full border border-[#E5E7EB] inline-block shrink-0" />}
                        <span className="line-clamp-2">{l.title}</span>
                      </button>
                    </li>
                  );
                })}
                {m.quiz && (
                  <li>
                    <Link to={`/quiz/${course.id}/${m.id}`} data-testid={`quiz-link-${m.id}`} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded-md text-[#064E3B] hover:bg-[#F3F4F6] flex items-center gap-1">
                      <ListChecks className="w-3.5 h-3.5" /> Module Quiz
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          ))}
          {course.final_test && (
            <Link to={`/final-test/${course.id}`} data-testid="link-final-test" className="block mt-4 mx-2 mb-2 px-3 py-2 rounded-md bg-[#064E3B] text-white text-sm font-semibold text-center flex items-center justify-center gap-1">
              <Trophy className="w-4 h-4" /> Final Test
            </Link>
          )}
        </div>
      </aside>

      <main className="lg:col-span-9 bg-white border border-[#E5E7EB] rounded-xl p-6 md:p-10">
        <div className="label-eyebrow">{activeLesson.moduleTitle}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2 tracking-tight">{activeLesson.title}</h1>

        <article className="prose max-w-none mt-6 text-[#111827] whitespace-pre-line leading-relaxed">
          {activeLesson.content}
        </article>

        {activeLesson.video_url && (
          <div className="mt-6 aspect-video rounded-xl overflow-hidden border border-[#E5E7EB]">
            <iframe className="w-full h-full" src={activeLesson.video_url} title={activeLesson.title} allowFullScreen />
          </div>
        )}

        {activeLesson.key_terms?.length > 0 && (
          <div className="mt-8 bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl p-5">
            <div className="label-eyebrow">Key terms</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {activeLesson.key_terms.map((k) => (
                <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-white border border-[#E5E7EB]">{k}</span>
              ))}
            </div>
          </div>
        )}

        {activeLesson.examples?.length > 0 && (
          <div className="mt-6">
            <div className="label-eyebrow">Examples</div>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-[#4B5563]">
              {activeLesson.examples.map((e, i) => <li key={`${e.slice(0, 32)}-${i}`}>{e}</li>)}
            </ul>
          </div>
        )}

        {activeLesson.summary && (
          <div className="mt-6 bg-[#064E3B] text-white rounded-xl p-5">
            <div className="label-eyebrow text-[#FECACA]">Summary</div>
            <p className="mt-2">{activeLesson.summary}</p>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-[#E5E7EB] pt-6">
          <button onClick={prevLesson} disabled={activeIndex === 0} data-testid="lesson-prev" className="btn-outline flex items-center gap-1 disabled:opacity-50">
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <div className="flex items-center gap-2">
            <button data-testid="lesson-mark-complete" onClick={markComplete} disabled={busy || completed.has(activeLesson.id)} className="btn-primary flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> {completed.has(activeLesson.id) ? "Completed" : "Mark complete"}
            </button>
            {lastInModule && (
              <Link to={`/quiz/${course.id}/${activeLesson.moduleId}`} data-testid="goto-quiz" className="btn-secondary flex items-center gap-1">
                <ListChecks className="w-4 h-4" /> Take Quiz
              </Link>
            )}
          </div>
          <button onClick={nextLesson} disabled={activeIndex === flatLessons.length - 1} data-testid="lesson-next" className="btn-outline flex items-center gap-1 disabled:opacity-50">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </main>

      <AITutor courseId={course.id} moduleId={activeLesson.moduleId} />
    </div>
  );
}
